import { PrimeMark } from '@/components/PrimeMark'
import { useState, type ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { BarChart3, Code2, GitMerge, LayoutList, MessageSquare, MessageSquarePlus } from 'lucide-react'
import { QUICK_CHAT } from '@/lib/lead'
import { api, engine } from '@/runtime'
import { useAgentSessions, useAllRuns, useRuns } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { isLive, isOver } from '@/kinds/agentrun/schema'
import { isLead, NewAgentSheet, repoName, useFactoryEnabled } from '@/kinds/agentrun/components'
import { useRunStatus } from '@/components/RunStatus'
import { SectionTitle, Tag, cx } from '@/components/ui'

// The Agents hub's list: Prime, the coding agents (what is going first, then
// idle ones, then the week's ended ones) and your other chats, in one place.
// On a desk it stands beside the thread; on the phone it is the side sheet.

/** What a page passes as `currentId` while the quick chat is open, whichever workspace's thread it is. */
export const QUICK_CHAT_ID = 'quick'

const ago = (iso: string | undefined, now: number) => {
  if (!iso) return ''
  const m = Math.round((now - Date.parse(iso)) / 60000)
  if (m < 1) return 'now'
  if (m < 60) return `${m}m`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.round(h / 24)}d`
}

const Row = ({
  active,
  onClick,
  children,
  testid,
  sessionId,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
  testid?: string
  sessionId?: string
}) => (
  <li>
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'w-full text-left rounded-[var(--radius-control)] px-3 py-2 min-h-11 hover:bg-raised',
        active && 'bg-raised',
      )}
      data-testid={testid}
      data-session-id={sessionId}
    >
      {children}
    </button>
  </li>
)

export const AgentList = ({ currentId, onPick }: { currentId?: string; onPick?: () => void }) => {
  const navigate = useNavigate()
  const sessions = useAgentSessions()
  const runs = useRuns() ?? []
  const factory = useFactoryEnabled()
  const now = useNow(60_000)
  const [starting, setStarting] = useState(false)
  // each agent's chip from the one model (lib/waiting.ts), as on Now and the Agents screen
  const statusOf = useRunStatus()
  const go = (to: string, params?: Record<string, string>, search?: Record<string, unknown>) => {
    onPick?.()
    void navigate({ to, params, search } as never)
  }
  const prime = sessions.find((s) => s.spec.prime && !s.deleted_at)
  // a workspace member's thread with her own Prime is read here, never carried on
  const memberThreads = sessions.filter((s) => s.labels.member && !s.deleted_at).slice(0, 10)
  // a Claude Code client's thread (an API token with mcp): its cards, tapped here
  const clientThreads = sessions.filter((s) => s.labels.client && !s.deleted_at).slice(0, 10)
  const chats = sessions
    .filter(
      (s) =>
        s.status.state === 'active' && !s.spec.prime && !s.labels.member && !s.labels.client && !s.deleted_at,
    )
    .slice(0, 20)
  // Prime on Claude Code (the lead) is Prime, not one of the coding agents
  const allRuns = useAllRuns() ?? []
  const [leadBusy, setLeadBusy] = useState(false)
  const startLead = async () => {
    if (leadBusy) return
    setLeadBusy(true)
    try {
      const st = await api.leadStart()
      await engine.sync()
      go('/agents/run/$id', { id: st.run_id })
    } catch {
      setLeadBusy(false)
    }
  }
  const lead = allRuns.find((r) => isLead(r) && !r.deleted_at && !isOver(r.spec.status))
  const coding = runs.filter((r) => r.spec.source.provider === 'factory' && !r.deleted_at && !isLead(r))
  const weekAgo = new Date(now - 7 * 86400_000).toISOString()
  const going = coding.filter((r) => isLive(r.spec.status))
  const idle = coding.filter((r) => r.spec.status === 'Suspended')
  const ended = coding
    .filter((r) => !isLive(r.spec.status) && r.spec.status !== 'Suspended' && r.spec.updated_at >= weekAgo)
    .slice(0, 10)

  const runRow = (r: (typeof coding)[number]) => {
    const st = statusOf(r)
    return (
      <Row
        key={r.id}
        active={r.id === currentId}
        onClick={() => go('/agents/run/$id', { id: r.id })}
        testid="agent-run-row"
      >
        <span className="flex items-center gap-2">
          <span className="flex-1 min-w-0 truncate text-sm">{r.spec.title || r.spec.name}</span>
          <span className="text-[11px] text-faint shrink-0">
            {ago(r.spec.last_active_at || r.spec.updated_at, now)}
          </span>
        </span>
        <span className="flex items-center gap-1.5 mt-0.5">
          <Tag tone={st.tone}>{st.word}</Tag>
          {r.spec.pr_number ? <Tag>PR #{r.spec.pr_number}</Tag> : null}
          <span className="text-[11px] text-faint truncate">{repoName(r.spec.repo_url)}</span>
        </span>
      </Row>
    )
  }

  return (
    <nav className="flex flex-col gap-4" aria-label="Agents" data-testid="agent-list">
      <ul className="flex flex-col gap-0.5">
        {factory && !lead && (
          <Row active={false} onClick={() => void startLead()} testid="agent-lead-start">
            <span className="flex items-center gap-2 text-sm">
              <PrimeMark size={15} aria-hidden />{' '}
              {leadBusy ? 'Starting Prime…' : 'Start Prime on Claude Code'}
            </span>
          </Row>
        )}
        {lead && (
          <Row
            active={lead.id === currentId}
            onClick={() => go('/agents/run/$id', { id: lead.id })}
            testid="agent-lead"
          >
            <span className="flex items-center gap-2 text-sm">
              <PrimeMark size={15} aria-hidden /> Prime
            </span>
          </Row>
        )}
        {/* the quick chat is always here: its thread is made the first time it is opened */}
        <Row
          active={currentId === QUICK_CHAT_ID || (!!prime && prime.id === currentId)}
          onClick={() =>
            prime ? go('/agents/$id', { id: prime.id }) : go('/agents', undefined, { quick: true })
          }
          testid="agent-prime"
        >
          <span className="flex items-center gap-2 text-sm">
            <MessageSquare size={15} aria-hidden /> {QUICK_CHAT}
          </span>
        </Row>
        <Row active={false} onClick={() => go('/agents', undefined, { fresh: true })}>
          <span className="flex items-center gap-2 text-sm">
            <MessageSquarePlus size={15} aria-hidden /> New chat
          </span>
        </Row>
        {factory && (
          <Row active={currentId === 'all'} onClick={() => go('/agents/all')} testid="agent-all-link">
            <span className="flex items-center gap-2 text-sm">
              <LayoutList size={15} aria-hidden /> All agents
            </span>
          </Row>
        )}
        {factory && (
          <Row active={currentId === 'stats'} onClick={() => go('/agents/stats')} testid="agent-stats-link">
            <span className="flex items-center gap-2 text-sm">
              <BarChart3 size={15} aria-hidden /> Stats
            </span>
          </Row>
        )}
        {factory && (
          <Row
            active={currentId === 'pipeline'}
            onClick={() => go('/agents/pipeline')}
            testid="agent-pipeline-link"
          >
            <span className="flex items-center gap-2 text-sm">
              <GitMerge size={15} aria-hidden /> Pipeline
            </span>
          </Row>
        )}
        {factory && (
          <Row active={false} onClick={() => setStarting(true)} testid="new-coding-agent">
            <span className="flex items-center gap-2 text-sm">
              <Code2 size={15} aria-hidden /> New coding agent
            </span>
          </Row>
        )}
      </ul>
      {(going.length > 0 || idle.length > 0 || ended.length > 0) && (
        <section>
          <SectionTitle>Coding agents</SectionTitle>
          <ul className="flex flex-col gap-0.5">
            {going.map(runRow)}
            {idle.map(runRow)}
            {ended.map(runRow)}
          </ul>
        </section>
      )}
      {memberThreads.length > 0 && (
        <section>
          <SectionTitle>Members' threads</SectionTitle>
          <ul className="flex flex-col gap-0.5" data-testid="member-threads">
            {memberThreads.map((s) => (
              <Row
                key={s.id}
                active={s.id === currentId}
                onClick={() => go('/agents/$id', { id: s.id })}
                sessionId={s.id}
              >
                <span className="flex items-center gap-2">
                  <span className="flex-1 min-w-0 truncate text-sm">{s.spec.title}</span>
                  <span className="text-[11px] text-faint shrink-0">{ago(s.status.last_at, now)}</span>
                </span>
              </Row>
            ))}
          </ul>
        </section>
      )}
      {clientThreads.length > 0 && (
        <section>
          <SectionTitle>Claude Code</SectionTitle>
          <ul className="flex flex-col gap-0.5" data-testid="client-threads">
            {clientThreads.map((s) => (
              <Row
                key={s.id}
                active={s.id === currentId}
                onClick={() => go('/agents/$id', { id: s.id })}
                sessionId={s.id}
              >
                <span className="flex items-center gap-2">
                  <span className="flex-1 min-w-0 truncate text-sm">
                    {s.spec.title.replace(/^Claude Code: /, '')}
                  </span>
                  <span className="text-[11px] text-faint shrink-0">{ago(s.status.last_at, now)}</span>
                </span>
              </Row>
            ))}
          </ul>
        </section>
      )}
      {chats.length > 0 && (
        <section>
          <SectionTitle>Chats</SectionTitle>
          <ul className="flex flex-col gap-0.5" data-testid="agent-sessions">
            {chats.map((s) => (
              <Row
                key={s.id}
                active={s.id === currentId}
                onClick={() => go('/agents/$id', { id: s.id })}
                sessionId={s.id}
              >
                <span className="flex items-center gap-2">
                  <span className="flex-1 min-w-0 truncate text-sm">{s.spec.title}</span>
                  <span className="text-[11px] text-faint shrink-0">{ago(s.status.last_at, now)}</span>
                </span>
              </Row>
            ))}
          </ul>
        </section>
      )}
      {starting && <NewAgentSheet onClose={() => setStarting(false)} session={currentId} />}
    </nav>
  )
}

/**
 * The hub's frame: on a desk, the list stands beside the thread; on a phone,
 * only the thread. With `docked` (the lead's artifacts beside its thread) the
 * artifacts take the list's room: thread and `side` share the width.
 */
export const AgentsLayout = ({
  currentId,
  children,
  side,
  docked = false,
}: {
  currentId?: string
  children: ReactNode
  side?: ReactNode
  docked?: boolean
}) =>
  docked ? (
    <div className="flex items-start gap-6" data-testid="agents-docked">
      {/* the page is full width while docked: the thread takes what the panel leaves */}
      <div className="min-w-0 flex-1">{children}</div>
      {side}
    </div>
  ) : (
    <div className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-8">
      <aside className="hidden lg:block">
        <div className="sticky top-3 max-h-[calc(100dvh-1.5rem)] overflow-y-auto pr-1">
          <AgentList currentId={currentId} />
        </div>
      </aside>
      {/* wide enough that a thread's top bar runs the width of the pane on a laptop */}
      <div className="min-w-0 max-w-5xl w-full">{children}</div>
      {side}
    </div>
  )
